import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'
export type DigitStyle = 'latn' | 'arab'

interface ThemeContextValue {
  mode: ThemeMode
  resolved: 'light' | 'dark'
  setMode: (m: ThemeMode) => void
  digits: DigitStyle
  setDigits: (d: DigitStyle) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const STORAGE_KEY = 'najah.theme'
const DIGITS_KEY = 'najah.digits'

function readStored<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T) ?? fallback
  } catch {
    // الوضع الخاص أو تخزين معطّل — نعود للافتراضي بدل الانهيار
    return fallback
  }
}

function systemPrefersDark() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored<ThemeMode>(STORAGE_KEY, 'system'))
  const [digits, setDigitsState] = useState<DigitStyle>(() => readStored<DigitStyle>(DIGITS_KEY, 'latn'))
  const [systemDark, setSystemDark] = useState(systemPrefersDark)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', resolved)
    root.style.colorScheme = resolved
  }, [resolved])

  useEffect(() => {
    document.documentElement.setAttribute('data-digits', digits)
  }, [digits])

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m)
    try { localStorage.setItem(STORAGE_KEY, m) } catch { /* تخزين معطّل */ }
  }, [])

  const setDigits = useCallback((d: DigitStyle) => {
    setDigitsState(d)
    try { localStorage.setItem(DIGITS_KEY, d) } catch { /* تخزين معطّل */ }
  }, [])

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode, digits, setDigits }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme يجب أن يُستعمل داخل ThemeProvider')
  return ctx
}
